//angular
import { Component, Input, Output, OnInit, EventEmitter, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
//app animations
import { AppAnimations } from 'src/app/_shared/animations';
//app data
import { VisibleState, ChargeStartingDateOptions, ProductRatePlanChargesCategory, collectionOfProductRatePlanChargesCategory } from 'src/app/app.data';
//app interfaces
import { DropDown } from 'src/app/_shared/interfaces/dropdown.interface';
//app models
import { CompanyCurrency } from 'src/app/_shared/models/company.model';
//app services
import { DataSharingService, UtilsService } from 'src/app/_core/services';
import { ValidatorService } from 'src/app/_shared/validators/validator.service';
//others
import { Unsubscriber } from 'src/app/_shared/unsubscriber/unsubscriber';

@Component({
  animations: [AppAnimations],
  host: { '[@smoothAppearance]': '', '[@slideInRightAnimation]': '' },
  selector: 'form-charge-details',
  templateUrl: './charge-details.component.html'
})

export class FormChargeDetailsComponent implements OnInit, AfterViewInit {
  cleaveNumDecimalOptions;
  cleaveNumIntegerOptions;
  collectionOfProductRatePlanChargesCategory: Array<DropDown> = collectionOfProductRatePlanChargesCategory;
  currencyDefault: CompanyCurrency;
  currencyDefaultId: number;
  currencyIso3: string;
  currencySymbol: string;
  form: FormGroup;
  isFreeShipping: boolean = true;
  selectedCategory: ProductRatePlanChargesCategory = ProductRatePlanChargesCategory.digital;
  selectedTimeFrameOption: ChargeStartingDateOptions;
  ratePlanCharge: any;
  isSubscription: boolean = false;
  isEditOnCreatedSubscription: boolean = false;
  lengthMeasurement: string;
  weightMeasurement: string;
  private subs = new Unsubscriber();

  @Input() _categoryValues = ProductRatePlanChargesCategory;
  @Input() _data: any;
  @Input() _isNewCharge: boolean = false;
  @Input() _visibleState = VisibleState;

  @Output() _emitBodyParams: EventEmitter<any> = new EventEmitter<any>();
  @Output() _emitFormErrors: EventEmitter<any> = new EventEmitter<any>();
  @Output() _getChargeType: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private _fb: FormBuilder,
    private _shareData: DataSharingService,
    private _validatorService: ValidatorService,
    private utils: UtilsService,
  ) {
    this.subs.addSub = this._shareData.getSettingsVariables().subscribe(([decimalPlaces, decimalToken, thousandsToken, _factoringDimension, lengthMeasurement, weightMeasurement]) => {
      this.cleaveNumDecimalOptions = {
        numeral: true,
        numeralPositiveOnly: true,
        stripLeadingZeroes: false,
        numeralDecimalScale: decimalPlaces,
        delimiter: thousandsToken,
        numeralDecimalMark: decimalToken
      };

      this.cleaveNumIntegerOptions = {
        numeral: true,
        numeralPositiveOnly: true,
        stripLeadingZeroes: false,
        numeralDecimalScale: 0,
        delimiter: thousandsToken,
        numeralDecimalMark: decimalToken
      };

      this.lengthMeasurement = lengthMeasurement;
      this.weightMeasurement = weightMeasurement;
    });
  }

  get detailsForm() {
    return this.form;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.ratePlanCharge = this._data.ratePlanCharge;
    //Subscribe to company currency base
    this.subs.addSub = this._shareData.currentCompanySettingsCurrencyBaseSource.subscribe(data => {
      let companyCurrencyBase = data.currencyBase;
      this.currencySymbol = companyCurrencyBase?.currency?.symbol;
      this.currencyIso3 = companyCurrencyBase?.currency?.iso3;
      this.currencyDefault = companyCurrencyBase;
      this.setUpForm();

      // edit
      if (this.ratePlanCharge?.id && this._data.inlineEdit) {
        // adds id
        this.form.addControl('id',new FormControl());
        this.form.get('id').setValue(this.ratePlanCharge?.id);
      }

      if (this._data.isSubscription) {
        // created subscription
        this.isSubscription = true;
        // edit
        if (this.ratePlanCharge?.id) {
          this.isEditOnCreatedSubscription = true;
          this.form.get('category').disable();
          if(this.form.get('category').value === ProductRatePlanChargesCategory.physical) {
            this.form.get('weight').disable();
            this.form.get('width').disable();
            this.form.get('height').disable();
            this.form.get('depth').disable();
          }
        }
      }
    })
  }

  ngAfterViewInit() {
    let previousValid = this.form.valid;
    this.form.valueChanges.subscribe(() => {
      if (this.form.valid !== previousValid) {
        this._emitFormErrors.emit(false);
      }
    });
  }

  onChangeCategory(_value: ProductRatePlanChargesCategory) {
    this.selectedCategory = _value;
    if (this.selectedCategory === ProductRatePlanChargesCategory.digital) {
      this.form.get('weight').setValue(0);
      this.form.get('weight').disable();
      this.form.get('width').disable();
      this.form.get('height').disable();
      this.form.get('depth').disable();
      this.form.get('isFreeShipping').disable();
    } else {
      this.form.get('weight').setValue('');
      this.form.get('weight').enable();
      this.form.get('width').setValue('');
      this.form.get('width').enable();
      this.form.get('height').setValue('');
      this.form.get('height').enable();
      this.form.get('depth').setValue('');
      this.form.get('depth').enable();
      this.form.get('isFreeShipping').enable();
    }
  }

  setBodyParams(): void {
    let bodyParams: any = {
      name: this.form.get('name').value,
      category: this.form.get('category').value,
      isFreeShipping: this.form.get('isFreeShipping').value,
      depth: this.utils.convertToNumber(this.form.get('depth').value),
      height: this.utils.convertToNumber(this.form.get('height').value),
      weight: this.utils.convertToNumber(this.form.get('weight').value),
      width: this.utils.convertToNumber(this.form.get('width').value),
    };

    if (this._data.inlineEdit) {
      if (this.ratePlanCharge?.id) {
        bodyParams.id = Number(this.form.get('id').value);
      }

      bodyParams.depth = this.form.get('depth').value;
      bodyParams.height = this.form.get('height').value;
      bodyParams.weight = this.form.get('weight').value;
      bodyParams.width = this.form.get('width').value;
    }

    if (this.isSubscription && this.ratePlanCharge?.id) {
      // edit
      // subscription
      delete bodyParams.category;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      this._emitFormErrors.emit(true);
      return;
    }

    this._emitBodyParams.emit(bodyParams);
  }

  setUpForm() {
    this.form = this._fb.group({
      category: [this.ratePlanCharge?.category || ProductRatePlanChargesCategory.digital,  [Validators.required]],
      depth: [this.ratePlanCharge?.depth ? this.ratePlanCharge?.depth : '', [this._validatorService.positiveDecimalNumberIncludeZero]],
      height: [this.ratePlanCharge?.height ? this.ratePlanCharge?.height : '', [this._validatorService.positiveDecimalNumberIncludeZero]],
      isFreeShipping: this.ratePlanCharge?.isFreeShipping,
      name: [this.ratePlanCharge?.name, [Validators.required, this._validatorService.emptySpace, Validators.minLength(3), Validators.maxLength(255)]],
      weight: [this.ratePlanCharge?.weight ? this.ratePlanCharge?.weight : '', [this._validatorService.positiveDecimalNumberIncludeZero]],
      width: [this.ratePlanCharge?.width ? this.ratePlanCharge?.width : '', [this._validatorService.positiveDecimalNumberIncludeZero]],
    });

    if (this.form.get('depth').value && this._data.inlineEdit) {
      this.form.get('depth').setValue(this.ratePlanCharge?.depth);
    }
    if (this.form.get('height').value && this._data.inlineEdit) {
      this.form.get('height').setValue(this.ratePlanCharge?.height);
    }
    if (this.form.get('weight').value && this._data.inlineEdit) {
      this.form.get('weight').setValue(this.ratePlanCharge?.weight);
    }
    if (this.form.get('width').value && this._data.inlineEdit) {
      this.form.get('width').setValue(this.ratePlanCharge?.width);
    }

    if (this.form.get('category').value === ProductRatePlanChargesCategory.digital) {
      this.form.get('weight').setValue(0);
      this.form.get('isFreeShipping').disable();
      this.form.get('isFreeShipping').setValue(true);
    } else {
      this.selectedCategory = this.form.get('category').value;
      this.form.get('weight').enable();
      this.form.get('width').enable();
      this.form.get('height').enable();
      this.form.get('depth').enable();
      this.form.get('isFreeShipping').enable();
    }
  }
}
